import { useState } from 'react';
import { Check, Copy, ExternalLink, FileText, Mail } from 'lucide-react';
import type { DataTableCellTone } from '@shared/types/views';
import { cn } from '@/lib/utils';
import { ToneDot } from '../atoms/ToneDot';
import { splitSubject, textParts, type DetailField } from './row-detail-model';

const isEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

/** A value with its links shown as their site, opened in a new tab. */
export const DetailText = ({ text }: { text: string }) => {
  if (isEmail(text)) {
    return <a href={`mailto:${text}`} className="text-domino-aubergine underline decoration-domino-aubergine/30 underline-offset-2 hover:decoration-domino-aubergine">{text}</a>;
  }
  return <>{textParts(text).map((part, index) => part.kind === 'text' ? <span key={index}>{part.text}</span> : (
    <a key={index} href={part.href} target="_blank" rel="noopener noreferrer" title={part.href}
      className="mx-0.5 inline-flex max-w-full items-center gap-1 rounded-full bg-domino-plumTint px-2 py-0.5 align-baseline text-xs font-medium text-domino-aubergine hover:bg-domino-aubergine hover:text-white">
      <span className="truncate">{part.label}</span>
      <ExternalLink aria-hidden="true" className="h-3 w-3 shrink-0" />
    </a>
  ))}</>;
};

const TONE_PILL: Record<DataTableCellTone, string> = {
  good: 'bg-[var(--domino-good-bg)] text-[var(--domino-good)]',
  warning: 'bg-[var(--domino-warning-bg)] text-[var(--domino-warning)]',
  critical: 'bg-[var(--domino-critical-bg)] text-[var(--domino-critical)]',
  neutral: 'bg-domino-white text-domino-ink',
};

/** A decision-like value: colored by the producer's tone, else highlighted without a reading. */
export const StatusPill = ({ field }: { field: DetailField }) => (
  <span className={cn('inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold',
    field.cell.tone ? TONE_PILL[field.cell.tone] : 'bg-domino-lilac text-domino-ink ring-1 ring-domino-sunSoft')}>
    {field.cell.tone && <ToneDot tone={field.cell.tone} />}
    <span className="sr-only">{field.label}{' :'} </span>
    {field.text}
  </span>
);

/** A multi-line text kept as written (an email draft, a note), with its subject apart and a copy button. */
export const DocumentCard = ({ field }: { field: DetailField }) => {
  const [copy, setCopy] = useState<'idle' | 'copied' | 'failed'>('idle');
  const { subject, body } = splitSubject(field.text);
  const Icon = subject ? Mail : FileText;
  const onCopy = async () => {
    try {
      if (!navigator.clipboard) throw new Error('clipboard_unavailable');
      await navigator.clipboard.writeText(field.text);
      setCopy('copied');
    } catch {
      setCopy('failed');
    }
  };
  return (
    <article aria-label={field.label} className="overflow-hidden rounded-xl border border-domino-cardBorder bg-domino-warm shadow-[var(--domino-shadow-soft)]">
      <div className="flex items-center justify-between gap-2 border-b border-domino-cardBorder bg-domino-white px-4 py-2">
        <h4 className="flex min-w-0 items-center gap-2 text-sm font-semibold text-domino-aubergine">
          <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
          <span className="truncate">{field.label}</span>
        </h4>
        <button type="button" onClick={() => void onCopy()}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-domino-aubergine transition hover:bg-domino-plumTint">
          {copy === 'copied' ? <Check aria-hidden="true" className="h-3.5 w-3.5" /> : <Copy aria-hidden="true" className="h-3.5 w-3.5" />}
          {copy === 'copied' ? 'Copié' : copy === 'failed' ? 'Copie impossible' : 'Copier'}
        </button>
      </div>
      {subject && (
        <p className="flex gap-3 border-b border-dashed border-domino-cardBorder px-4 py-2.5 text-sm">
          <span className="shrink-0 text-domino-inkSoft">Objet</span>
          <span className="font-semibold text-domino-ink">{subject}</span>
        </p>
      )}
      <p className="whitespace-pre-wrap px-4 py-3 text-[0.9375rem] leading-7 text-domino-ink"><DetailText text={body} /></p>
    </article>
  );
};
